import { forwardRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  AppBar,
  Toolbar,
  Box,
  SvgIcon,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { alpha, useTheme } from "@mui/material/styles";
import { faBars } from "@fortawesome/pro-light-svg-icons";
import { useAuth } from "@context/shared/AuthContext";
import { isAccreditor, isAuthor } from "@config/shared/roles";
import { TabBar } from "./tab-bar";
import { DarkModeButton } from "./dark-mode-button";
import { AccountButton } from "./account-button";
import { LanguageSwitch } from "./language-switch";
import { NotificationsButton } from "./notifications-button";
import { FavouritesButton } from "./favourites-button";
import { ToolboxButton } from "./toolbox-button";
import { SearchButton } from "./search-button";
import { HelpButton } from "./help-button";
import { GlossaryButton } from "./glossary-button";
import { AdminButton } from "./admin-button";
import { TenantLogo } from "@components/shared/branding/tenant-logo";

const NavDivider = () => (
  <Box
    sx={{
      width: "1px",
      height: "28px",
      bgcolor: (t) => alpha(t.palette.common.white, 0.3),
      mx: 1,
      alignSelf: "center",
    }}
  />
);

const TopNav = forwardRef((props, ref) => {
  const navigate = useNavigate();
  const location = useLocation();
  const pathname = location.pathname;
  const theme = useTheme();
  const isMd = useMediaQuery(theme.breakpoints.up("md"));

  const { t } = useTranslation();
  const { user } = useAuth();
  const accreditor = isAccreditor(user?.roleId);
  // Only roles that may open admin get the way in; anyone else would just
  // land on the 403 page.
  const canOpenAdmin = isAuthor(user?.roleId);
  const [anchorElNav, setAnchorElNav] = useState(null);

  const handleOpenNavMenu = (event) => { document.activeElement?.blur(); setAnchorElNav(event.currentTarget); };
  const handleCloseNavMenu = () => { document.activeElement?.blur(); setAnchorElNav(null); };

  const navItems = [
    { text: t("client.help"), url: "/help" },
    { text: t("client.glossary"), url: "/glossary" },
    ...(canOpenAdmin ? [{ text: t("client.admin"), url: "/admin" }] : []),
  ];

  const isPathnameMatch = navItems.some((item) => pathname.startsWith(item.url));

  return (
    <AppBar
      ref={ref}
      component="nav"
      sx={{
        backgroundColor: theme.palette.brand2,
        maxHeight: { xs: 350, md: 350 },
      }}
    >
      <Toolbar>
        {isMd ? (
          <Box
            sx={{
              backdropFilter: "blur(6px)",
              position: "sticky",
              top: 0,
              width: "100%",
              zIndex: (t) => t.zIndex.appBar,
            }}
          >
            <Stack
              direction="row"
              sx={{ justifyContent: "space-between", height: "56px", alignItems: "center" }}
            >
              <TenantLogo
                height={50}
                maxWidth={200}
                alt="Logo"
                fallback={
                  <Box sx={{ color: "common.white", fontSize: 24, fontWeight: 700, letterSpacing: "0.5px" }}>
                    CONNEXIO
                  </Box>
                }
              />

              <Stack direction="row" sx={{ alignItems: "center", gap: 1.5 }}>
                {accreditor ? (
                  <>
                    <LanguageSwitch />
                    <DarkModeButton />
                    <AccountButton />
                  </>
                ) : (
                  <>
                    {/* Group 1: Global utility */}
                    <SearchButton />

                    <NavDivider />

                    {/* Group 2: App controls */}
                    <ToolboxButton />
                    <LanguageSwitch />
                    <DarkModeButton />
                    <HelpButton />
                    <GlossaryButton />

                    <NavDivider />

                    {/* Group 3: Personal / account */}
                    <NotificationsButton />
                    <FavouritesButton />
                    {canOpenAdmin && <AdminButton />}
                    <AccountButton />
                  </>
                )}
              </Stack>
            </Stack>
          </Box>
        ) : (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexGrow: 1,
            }}
          >
            <TenantLogo
              height={36}
              maxWidth={120}
              alt="Logo"
              fallback={
                <Box sx={{ color: "common.white", fontSize: 16, fontWeight: 700, letterSpacing: "0.5px" }}>
                  CONNEXIO
                </Box>
              }
            />
            <Stack
              direction="row"
              sx={{
                alignItems: "center",
                overflowX: "auto",
                // extra room so the notification badge isn't clipped by overflow-x
                pt: "10px",
                mt: "-10px",
              }}
            >
              {accreditor ? (
                <>
                  <LanguageSwitch />
                  <DarkModeButton />
                  <AccountButton />
                </>
              ) : (
                <>
                  <SearchButton />
                  <ToolboxButton />
                  <LanguageSwitch />
                  <DarkModeButton />
                  <NotificationsButton />
                  <FavouritesButton />
                  <AccountButton />
                  <IconButton
                    onClick={handleOpenNavMenu}
                    sx={{
                      backgroundColor: isPathnameMatch ? alpha(theme.palette.common.white, 0.1) : "transparent",
                    }}
                  >
                    <SvgIcon>
                      <FontAwesomeIcon icon={faBars} color="white" />
                    </SvgIcon>
                  </IconButton>
                  <Menu
                    anchorEl={anchorElNav}
                    anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                    keepMounted
                    transformOrigin={{ vertical: "top", horizontal: "right" }}
                    open={Boolean(anchorElNav)}
                    onClose={handleCloseNavMenu}
                  >
                    {navItems.map((item) => (
                      <MenuItem
                        key={item.text}
                        onClick={() => { handleCloseNavMenu(); navigate(item.url); }}
                      >
                        <Typography sx={{ textAlign: "center" }}>{item.text}</Typography>
                      </MenuItem>
                    ))}
                  </Menu>
                </>
              )}
            </Stack>
          </Box>
        )}
      </Toolbar>
      {!accreditor && <TabBar />}
    </AppBar>
  );
});

TopNav.displayName = "TopNav";

export { TopNav };
